import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api/client'
import { asList } from '@/lib/api/list'
import { useAuth } from '@/lib/auth/AuthProvider'
import type { Demo, DemoReport, Followup, InstallReport, MySummary } from '../shared/types'
import { defaultWidgets, sanitizeWidgets } from './layout/model'
import type { CalendarEvent, DashNote, DashPayment, DashProject, DashTask, DashboardLayout, FinanceSummary, NoteColor, WidgetConfig, WidgetTone, WidgetType } from './types'
import { WIDGET_TONES, WIDGET_TYPES } from './types'

export { useMeetings } from '../meetings/api'

const TO_API: Record<WidgetType, string> = {
  kpiOpenTasks: 'open_tasks',
  kpiOverdueTasks: 'overdue_tasks',
  kpiOverdueFollowups: 'overdue_followups',
  kpiActiveDemos: 'active_demos',
  myTasks: 'my_tasks',
  payments: 'payments',
  notes: 'notes',
  calendar: 'calendar',
  projects: 'projects',
  meetings: 'meetings',
  followups: 'followups',
  endingDemos: 'completed_demos',
  installHealth: 'install_health',
  financeSummary: 'finance_summary',
}

const FROM_API = Object.fromEntries(Object.entries(TO_API).map(([ui, apiType]) => [apiType, ui])) as Record<string, WidgetType>

type ApiWidget = { id?: string; type?: string; width?: number; height?: number; view?: string; color?: string; density?: string }

function toApiWidget(widget: WidgetConfig): ApiWidget {
  return { id: widget.id, type: TO_API[widget.type], width: widget.w, height: widget.h, view: widget.view, color: widget.tone, density: widget.density }
}

function fromApiWidget(raw: ApiWidget): Partial<WidgetConfig> | null {
  const type = raw.type ? FROM_API[raw.type] ?? ((WIDGET_TYPES as readonly string[]).includes(raw.type) ? (raw.type as WidgetType) : undefined) : undefined
  if (!type) return null
  const tone = WIDGET_TONES.includes(raw.color as WidgetTone) ? (raw.color as WidgetTone) : undefined
  return {
    id: raw.id,
    type,
    w: raw.width as WidgetConfig['w'],
    h: raw.height as WidgetConfig['h'],
    view: raw.view === 'card' ? 'card' : 'list',
    tone,
    density: raw.density === 'compact' ? 'compact' : 'comfortable',
  }
}

export const useInstallReport = (enabled: boolean) =>
  useQuery({ queryKey: ['reports', 'installations', '', ''], queryFn: () => api.get<InstallReport>('/api/v1/reports/installations', {}), enabled })
export const useDemoReport = (enabled: boolean) =>
  useQuery({ queryKey: ['reports', 'demo-conversion', '', ''], queryFn: () => api.get<DemoReport>('/api/v1/reports/demo-conversion', {}), enabled })
export const useMySummary = (enabled: boolean) =>
  useQuery({ queryKey: ['tasks', 'mine', 'summary'], queryFn: () => api.get<MySummary>('/api/v1/tasks/mine/summary'), enabled })
export const useMyFollowups = (enabled: boolean) =>
  useQuery({
    queryKey: ['followups', 'mine', 'open'],
    queryFn: async () => asList(await api.get<Followup[] | { items?: Followup[] }>('/api/v1/followups', { mine: 1, status: 'open' })),
    enabled,
  })
export const useActiveDemos = (enabled: boolean) =>
  useQuery({ queryKey: ['demos', 'aktif'], queryFn: async () => asList(await api.get<Demo[] | { items?: Demo[] }>('/api/v1/demos', { status: 'aktif' })), enabled })

const keys = {
  layout: (userId: string) => ['dashboard', 'layout', userId] as const,
  tasks: ['dashboard', 'tasks'] as const,
  payments: ['dashboard', 'payments'] as const,
  notes: ['dashboard', 'notes'] as const,
  events: ['dashboard', 'events'] as const,
  projects: ['dashboard', 'projects'] as const,
  finance: ['dashboard', 'finance'] as const,
}

export function useDashboardLayout() {
  const { user, can } = useAuth()
  const userId = user?.id ?? 'anon'
  return useQuery({
    queryKey: keys.layout(userId),
    queryFn: async () => {
      const data = await api.get<{ widgets?: ApiWidget[]; updated_at?: string | null; updatedAt?: string | null }>('/api/v1/me/dashboard-layout')
      return { widgets: data?.widgets ?? null, updatedAt: data?.updated_at ?? data?.updatedAt ?? null }
    },
    select: (data): DashboardLayout => ({
      widgets: data.widgets == null ? sanitizeWidgets(defaultWidgets(), can) : sanitizeWidgets(data.widgets.map(fromApiWidget).filter(Boolean), can),
      updatedAt: data.updatedAt,
    }),
  })
}

export function useSaveDashboardLayout() {
  const { user } = useAuth()
  const userId = user?.id ?? 'anon'
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (widgets: WidgetConfig[]) => api.put('/api/v1/me/dashboard-layout', { widgets: widgets.map(toApiWidget) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.layout(userId) }),
  })
}

/** Firma sahibi: düzeni bir rolün varsayılanı yapar; kendi düzenini kaydetmemiş kullanıcılar bununla başlar. */
export function useSaveRoleLayout() {
  return useMutation({
    mutationFn: ({ roleId, widgets }: { roleId: string; widgets: WidgetConfig[] }) =>
      api.put(`/api/v1/me/dashboard-role-layouts/${roleId}`, { widgets: widgets.map(toApiWidget) }),
  })
}

function str(v: unknown, fallback = '') {
  return typeof v === 'string' ? v : fallback
}

export const useDashTasks = () =>
  useQuery({
    queryKey: keys.tasks,
    queryFn: async () => {
      const rows = asList(await api.get<Record<string, unknown>[] | { items?: Record<string, unknown>[] }>('/api/v1/tasks', { mine: 1 }))
      return rows.map((row): DashTask => ({
        id: str(row.id),
        title: str(row.title),
        due_on: str(row.due_on ?? row.due_at, ''),
        high_priority: row.priority === 'high' || row.priority === 'urgent' || row.high_priority === true,
        done: row.status === 'done' || row.done === true,
      }))
    },
  })

export function useToggleDashTask() {
  return useMutation({ mutationFn: async (_id: string) => undefined })
}

export const useDashPayments = () =>
  useQuery({
    queryKey: keys.payments,
    queryFn: async () => {
      const rows = asList(await api.get<Record<string, unknown>[] | { items?: Record<string, unknown>[] }>('/api/v1/me/dashboard/payments'))
      return rows.map((row): DashPayment => ({
        id: str(row.id),
        title: str(row.title),
        party: str(row.party ?? row.account_title),
        due_on: str(row.due_on ?? row.due_at),
        amount: str(row.amount, '0'),
        currency: str(row.currency, 'TRY'),
        direction: row.direction === 'out' ? 'out' : 'in',
      }))
    },
  })

export const useDashNotes = () =>
  useQuery({
    queryKey: keys.notes,
    queryFn: async () => {
      const rows = asList(await api.get<Record<string, unknown>[] | { items?: Record<string, unknown>[] }>('/api/v1/me/dashboard/notes'))
      return rows.map((row): DashNote => ({
        id: str(row.id),
        text: str(row.text ?? row.body),
        created_at: str(row.created_at ?? row.createdAt, new Date().toISOString()),
        color: (['primary', 'warning', 'success', 'violet'] as const).includes(row.color as NoteColor) ? (row.color as NoteColor) : 'primary',
      }))
    },
  })

export function useAddDashNote() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (text: string) => api.post('/api/v1/me/dashboard/notes', { text }),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.notes }),
  })
}

export function useDeleteDashNote() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.del(`/api/v1/me/dashboard/notes/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.notes }),
  })
}

export const useCalendarEvents = () =>
  useQuery({
    queryKey: keys.events,
    queryFn: async () => {
      const rows = asList(await api.get<Record<string, unknown>[] | { items?: Record<string, unknown>[] }>('/api/v1/me/dashboard/calendar'))
      return rows.map((row): CalendarEvent => ({
        id: str(row.id),
        title: str(row.title),
        subtitle: str(row.subtitle),
        starts_at: str(row.starts_at ?? row.planned_at),
        kind: (['meeting', 'demo', 'review', 'call'] as const).includes(row.kind as CalendarEvent['kind']) ? (row.kind as CalendarEvent['kind']) : 'meeting',
        online: row.online === true,
      }))
    },
  })

export const useDashProjects = () =>
  useQuery({
    queryKey: keys.projects,
    queryFn: async () => {
      const rows = asList(await api.get<Record<string, unknown>[] | { items?: Record<string, unknown>[] }>('/api/v1/me/dashboard/projects'))
      return rows.map((row): DashProject => ({
        id: str(row.id),
        name: str(row.name ?? row.title),
        status: (['active', 'planning', 'waiting', 'done'] as const).includes(row.status as DashProject['status']) ? (row.status as DashProject['status']) : 'active',
        progress: typeof row.progress === 'number' ? row.progress : 0,
        due_on: str(row.due_on ?? row.end_date),
        members: Array.isArray(row.members) ? row.members.filter((m): m is string => typeof m === 'string') : [],
        extra: typeof row.extra === 'number' ? row.extra : 0,
      }))
    },
  })

export const useFinanceSummary = () =>
  useQuery({
    queryKey: keys.finance,
    queryFn: async () => {
      const row = await api.get<Record<string, unknown>>('/api/v1/me/dashboard/finance-summary')
      const trend = Array.isArray(row?.trend) ? row.trend : []
      return {
        currency: str(row?.currency, 'TRY'),
        income: str(row?.income, '0'),
        expense: str(row?.expense, '0'),
        net: str(row?.net, '0'),
        cash: str(row?.cash, '0'),
        trend: trend.map((item) => {
          const point = item as Record<string, unknown>
          return { month: str(point.month), income: Number(point.income) || 0, expense: Number(point.expense) || 0 }
        }),
      } satisfies FinanceSummary
    },
  })
